/* eslint-disable max-len */
import React, { useContext } from 'react'
import { Button, Card, Typography, App } from 'antd'
import { useHistory } from 'react-router-dom'

import { getOnboardingLink } from '../../util/get-onboarding-link'
import { openChat } from '../../util/set-crisp-session-info'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectLocaleSlice } from '../../store/locale-slice'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { getApproverOnboardingProgress, shouldShowApproverOnboardingProgress } from '../../util/approver-onboarding-storage'
import { getOnboardingV2Progress, shouldShowOnboardingV2Progress } from '../../util/onboarding-v2-storage'

import { DocsDrawerNavbar } from '../docs-drawer-content/DocsDrawerNavbar'
import IntlMessages from '../../util/IntlMessages'
import { ApproverOnboardingProgressButton } from '../approver-onboarding-progress-button'
import { MenuContext } from '../../context/menuContext'
import { OnboardingV2ProgressButton } from '../onboarding-v2-progress-button'
import { OnboardingContext } from '../../context/onboardingContext'
import YoutubeEmbed from '../youtube-embed'

import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { FrontendUrls } from '../../types/urls'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

const { Paragraph } = Typography

interface HelpDrawerContentProps {
  onClose?: () => void
}

export const HelpDrawerContent: React.FC<HelpDrawerContentProps> = ({ onClose }) => {
  const history = useHistory()
  const { modal } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const onboardingV2Enabled = useFeatureFlagEnabled(FeatureFlagEnum.onboardingV2)
  const { navCollapsed, setNavCollapsed, collapseNavbar, setChangeSelectedKeys } = useContext(MenuContext)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [onboardingConfig, setOnboardingConfig] = useContext(OnboardingContext)

  // Check if we should show approver onboarding
  const approverProgress = getApproverOnboardingProgress(authUser.id, authCompany?.id as string)
  const isUserApprover = (authUser.approverToTeams?.length || 0) > 0
  const shouldShowApproverOnboarding = shouldShowApproverOnboardingProgress(approverProgress, isUserApprover, authCompany?.plan)

  // Check if we should show admin onboarding (OnboardingV2)
  const onboardingV2Progress = getOnboardingV2Progress(authUser.id, authCompany?.id as string)
  const shouldShowAdminOnboarding = onboardingV2Enabled && shouldShowOnboardingV2Progress(onboardingV2Progress) && authUser.role === 'Admin'

  const onSupportChatOpen = () => {
    openChat()
  }

  const handleApproverOnboarding = () => {
    import('../approver-onboarding-modal').then(({ showApproverOnboardingModal }) => {
      // Get all department names where user is an approver
      const departmentNames = authUser?.approverToTeams
        ?.map(team => team.name)
        .filter((name): name is string => Boolean(name && typeof name === 'string')) || []
      showApproverOnboardingModal(modal, authUser, locale, departmentNames, {
        companyId: authCompany?.id || '',
      })
    }).catch(() => {
      const hiddenDiv = document.createElement('div')
      hiddenDiv.style.display = 'none'
      document.body.appendChild(hiddenDiv)
      import('react-dom/client').then(ReactDOMClient => {
        const root = ReactDOMClient.createRoot(hiddenDiv)
        root.render(React.createElement(ApproverOnboardingProgressButton, {
          authUser, locale, companyId: authCompany?.id || '', companyPlan: authCompany?.plan,
        }))
        setTimeout(() => {
          const button = hiddenDiv.querySelector('button')
          if (button) button.click()
          root.unmount()
          document.body.removeChild(hiddenDiv)
        }, 10)
      })
    })
  }

  const startQuickTour = () => {
    if (onClose) {
      onClose()
    }
    setChangeSelectedKeys(true)
    if (collapseNavbar && !navCollapsed) {
      setNavCollapsed(true)
      setTimeout(() => {
        setOnboardingConfig({
          run: true,
          restart: true,
          role: authUser.role.toLowerCase(),
        })
        setChangeSelectedKeys(false)
      }, 800)
    } else {
      setOnboardingConfig({
        run: true,
        restart: true,
        role: authUser.role.toLowerCase(),
      })
      setTimeout(() => {
        setChangeSelectedKeys(false)
      }, 800)
    }
  }

  const handleHomeNavigation = () => {
    if (onClose) {
      onClose()
    }
    history.push(FrontendUrls.dashboard)
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <div className="help-drawer-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navbar Header */}
      <DocsDrawerNavbar
        title={<IntlMessages id="components.helpDrawer.needhelp" />}
        showHomeButton={true}
        showOpenInNewTab={false}
        onHomeNavigation={handleHomeNavigation}
        onClose={handleClose}
      />

      {/* Content */}
      <div className="help-drawer-inner" style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Account Manager Card - Admin only */}
        {authUser.role === 'Admin' && (
          <Card
            title={<IntlMessages id="components.helpDrawer.accountManager.title" />}
            className="help-drawer-card"
          >
            <YoutubeEmbed embedId="ISEPxcwWfSo" title="Meet Carmen - Your Account Manager" />
            <Paragraph style={{ whiteSpace: 'break-spaces', marginTop: '12px' }}>
              <IntlMessages id="components.helpDrawer.accountManager.carmenInfo" />
            </Paragraph>
            <Button
              type="primary"
              block
              href="https://calendly.com/carmen-vacationtracker/one-on-one/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: '12px' }}
            >
              <IntlMessages id="components.helpDrawer.bookACall" />
            </Button>
          </Card>
        )}

        <Card
          title={<IntlMessages id="app.helpdesk" />}
          extra={
            <a href="https://vacationtracker.io/helpdesk/" target="_blank" rel="noreferrer">
              <IntlMessages id="app.visitHelpdesk" />
            </a>
          }
          className="help-drawer-card"
        >
          <Paragraph style={{ whiteSpace: 'break-spaces' }}>
            <IntlMessages id="components.helpDrawer.helpDeskInfo" />
          </Paragraph>
        </Card>

        <Card
          title={<IntlMessages id="components.helpDrawer.supportTitle" />}
          extra={(window && (window as unknown as { $crisp: unknown }).$crisp) ?
            <Button type="link" onClick={onSupportChatOpen}>
              <IntlMessages id="app.contactSupport" />
            </Button> :
            <a href="https://vacationtracker.io/helpdesk/" target="_blank" rel="noreferrer">
              <IntlMessages id="app.contactSupport" />
            </a>
          }
          className="help-drawer-card"
        >
          <Paragraph style={{ whiteSpace: 'break-spaces' }}>
            <IntlMessages
              id="components.helpDrawer.supportInfo"
              values={{
                link: (...chunks) => <a href="mailto:hello@vacationtracker.io">{chunks}</a>,
              }}
            />
          </Paragraph>
        </Card>

        {!onboardingV2Enabled && (
          <Card
            title={<IntlMessages id="components.helpDrawer.quickTourTitle" />}
            extra={<Button type="link" className="joyride-start" onClick={startQuickTour} style={{ paddingLeft: 0 }}>
              <IntlMessages id="app.takeQuickTour" />
            </Button>}
            className="help-drawer-card"
          >
            <Paragraph style={{ whiteSpace: 'break-spaces' }}>
              <IntlMessages id="components.helpDrawer.quickTourInfo" />
            </Paragraph>
          </Card>
        )}

        {shouldShowApproverOnboarding && (
          <Card
            title={<IntlMessages id="components.helpDrawer.quickTourTitle" />}
            extra={<Button type="link" className="joyride-start" onClick={handleApproverOnboarding} style={{ paddingLeft: 0 }}>
              <IntlMessages id="app.takeQuickTour" />
            </Button>}
            className="help-drawer-card"
          >
            <ApproverOnboardingProgressButton
              authUser={authUser}
              locale={locale}
              companyId={authCompany?.id || ''}
              companyPlan={authCompany?.plan}
            />
          </Card>
        )}

        {shouldShowAdminOnboarding && (
          <Card
            title={<IntlMessages id="components.helpDrawer.quickTourTitle" />}
            extra={<Button type="link" className="joyride-start" onClick={startQuickTour} style={{ paddingLeft: 0 }}>
              <IntlMessages id="app.takeQuickTour" />
            </Button>}
            className="help-drawer-card"
          >
            <OnboardingV2ProgressButton
              authUser={authUser}
              locale={locale}
              heatmapData={{
                locations: authUser?.location ? [{ id: authUser.location.id, name: authUser.location.name }] : [],
                departments: authUser?.team ? [{ id: authUser.team.id, name: authUser.team.name }] : [],
                labels: [],
                holidays: [],
              }}
            />
          </Card>
        )}

        <Card
          title={
            authUser.role === 'User' ? <IntlMessages id="components.helpDrawer.userProductTourTitle" /> :
              authUser.role === 'Approver' ? <IntlMessages id="components.helpDrawer.approverProductTourTitle" /> :
                <IntlMessages id="components.helpDrawer.adminProductTourTitle" />
          }
          extra={
            <a href={getOnboardingLink(authUser.platform, authUser.role)} target="_blank" rel="noreferrer">
              <IntlMessages id="app.learnMore" />
            </a>
          }
          className="help-drawer-card"
        >
          {authUser.platform !== 'google' &&
            <Paragraph style={{ whiteSpace: 'break-spaces' }}>
              { authUser.role === 'User' && <IntlMessages id="components.helpDrawer.userProductTourInfo" /> }
              { authUser.role === 'Approver' && <IntlMessages id="components.helpDrawer.approverProductTourInfo" /> }
              { authUser.role === 'Admin' && <IntlMessages id="components.helpDrawer.adminProductTourInfo" /> }
              <a href="mailto:hello@vacationtracker.io">hello@vacationtracker.io</a>.
            </Paragraph>
          }
          {authUser.platform === 'google' &&
            <Paragraph style={{ whiteSpace: 'break-spaces' }}>
              { authUser.role === 'User' && <IntlMessages id="components.helpDrawer.google.userProductTourInfo" /> }
              { authUser.role === 'Approver' && <IntlMessages id="components.helpDrawer.google.approverProductTourInfo" /> }
              { authUser.role === 'Admin' && <IntlMessages id="components.helpDrawer.google.adminProductTourInfo" /> }
              <a href="mailto:hello@vacationtracker.io">hello@vacationtracker.io</a>.
            </Paragraph>
          }
        </Card>
      </div>
    </div>
  )
}
